/**
 * DV-DEP-001: Deployments API Service
 *
 * API service for Deployments functionality following the existing
 * dashboard API patterns with cookie-based authentication.
 */

import { apiRequest } from '@/lib/api-client';
import type {
  ProductionsResponse,
  ProductionResponse,
  YoutubeIntegrationResponse,
  CreateYoutubeUploadRequest,
  CreateYoutubeUploadResponse,
  DownloadUrlResponse,
  ListProductionsParams,
  ProductionJobsResponse,
} from '@/types/deployments';

/**
 * Deployments API Service
 *
 * Note: These are API contracts for backend implementation.
 * Backend endpoints need to be implemented separately.
 */
export class DeploymentsAPIService {
  // === PRODUCTIONS ===

  /**
   * List finished productions with optional filtering and pagination
   */
  static async getProductions(params?: ListProductionsParams): Promise<ProductionsResponse> {
    const searchParams = new URLSearchParams();

    // Always filter to finished productions only
    searchParams.set('status', 'finished');

    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());

    // Filter parameters (standardized naming)
    if (params?.filters?.aspectRatio) {
      searchParams.set('aspect_ratio', params.filters.aspectRatio);
    }
    if (params?.filters?.search) {
      searchParams.set('search', params.filters.search);
    }
    if (params?.filters?.dateFrom) {
      searchParams.set('date_from', params.filters.dateFrom);
    }
    if (params?.filters?.dateTo) {
      searchParams.set('date_to', params.filters.dateTo);
    }

    // Sort parameters (standardized naming)
    if (params?.sort?.field) {
      searchParams.set('sort_field', params.sort.field);
    }
    if (params?.sort?.direction) {
      searchParams.set('sort_direction', params.sort.direction);
    }

    const query = searchParams.toString();
    const endpoint = query ? `/api/deployments/productions?${query}` : '/api/deployments/productions';

    return apiRequest<ProductionsResponse>(endpoint);
  }

  /**
   * Get a specific production with its deployments
   */
  static async getProduction(productionId: string): Promise<ProductionResponse> {
    return apiRequest<ProductionResponse>(`/api/deployments/productions/${productionId}`);
  }

  /**
   * Get signed download URL for a production
   */
  static async getProductionDownloadUrl(productionId: string): Promise<DownloadUrlResponse> {
    return apiRequest<DownloadUrlResponse>(`/api/deployments/productions/${productionId}/download`);
  }

  // === YOUTUBE INTEGRATION ===

  /**
   * Get YouTube integration status for the current user
   */
  static async getYoutubeIntegrationStatus(): Promise<YoutubeIntegrationResponse> {
    return apiRequest<YoutubeIntegrationResponse>('/api/integrations/youtube');
  }

  /**
   * Disconnect YouTube integration
   */
  static async disconnectYoutubeIntegration(): Promise<void> {
    return apiRequest<void>('/api/integrations/youtube', {
      method: 'DELETE',
    });
  }

  /**
   * Initiate YouTube OAuth flow
   * Returns the OAuth URL that the user should be redirected to
   */
  static async initiateYoutubeOAuth(): Promise<{ authUrl: string }> {
    return apiRequest<{ authUrl: string }>('/api/integrations/youtube/connect');
  }

  // === DEPLOYMENTS ===

  /**
   * Create a YouTube upload/deployment
   */
  static async createYoutubeUpload(
    productionId: string,
    data: CreateYoutubeUploadRequest
  ): Promise<CreateYoutubeUploadResponse> {
    return apiRequest<CreateYoutubeUploadResponse>(`/api/deployments/productions/${productionId}/youtube`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /**
   * Get deployment status for a specific deployment
   */
  static async getDeploymentStatus(deploymentId: string): Promise<{
    deployment: {
      id: string;
      status: 'pending' | 'uploading' | 'processing' | 'published' | 'failed' | 'scheduled';
      progress?: number;
      error?: string;
      platformUrl?: string;
      updatedAt: string;
    };
  }> {
    return apiRequest(`/api/deployments/${deploymentId}/status`);
  }

  /**
   * Cancel a pending or in-progress deployment
   */
  static async cancelDeployment(deploymentId: string): Promise<void> {
    return apiRequest<void>(`/api/deployments/${deploymentId}/cancel`, {
      method: 'POST',
    });
  }

  /**
   * Retry a failed deployment
   */
  static async retryDeployment(deploymentId: string): Promise<CreateYoutubeUploadResponse> {
    return apiRequest<CreateYoutubeUploadResponse>(`/api/deployments/${deploymentId}/retry`, {
      method: 'POST',
    });
  }

  // === UTILITY METHODS ===

  /**
   * Helper to trigger file download in browser
   */
  static async triggerDownload(productionId: string, filename?: string): Promise<void> {
    try {
      const { downloadUrl } = await this.getProductionDownloadUrl(productionId);

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || `production-${productionId}.mp4`;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  }

  /**
   * Get jobs for a specific production
   * DV-DEP-003: Job tracking and scheduling
   */
  static async getProductionJobs(productionId: string): Promise<ProductionJobsResponse> {
    return apiRequest<ProductionJobsResponse>(`/api/deployments/productions/${productionId}/jobs`);
  }

  /**
   * Helper to format production for UI display
   */
  static formatProductionForDisplay(production: any): any {
    return {
      ...production,
      shortHint: production.aspectRatio === '9:16' && production.duration <= 60 ? 'Short' : undefined,
    };
  }

  /**
   * Helper to validate YouTube upload data
   */
  static validateYoutubeUploadData(data: CreateYoutubeUploadRequest): string[] {
    const errors: string[] = [];

    if (!data.title?.trim()) {
      errors.push('Title is required');
    }

    if (data.title && data.title.length > 100) {
      errors.push('Title must be 100 characters or less');
    }

    if (data.description && data.description.length > 5000) {
      errors.push('Description must be 5000 characters or less');
    }

    if (data.tags && data.tags.length > 15) {
      errors.push('Maximum 15 tags allowed');
    }

    if (data.scheduledAt) {
      const scheduledDate = new Date(data.scheduledAt);
      const now = new Date();

      if (scheduledDate <= now) {
        errors.push('Scheduled time must be in the future');
      }

      // YouTube requires at least 15 minutes in the future
      const minTime = new Date(now.getTime() + 15 * 60 * 1000);
      if (scheduledDate < minTime) {
        errors.push('Scheduled time must be at least 15 minutes in the future');
      }
    }

    return errors;
  }
}